import React, { useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const inp = { border:'1px solid #e2e8f0', borderRadius:'8px', padding:'8px 12px', fontSize:'13px', outline:'none', background:'var(--bg,#fff)' };
const card = { background:'white', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', marginBottom:'12px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' };

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function MonthlyReport() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth()+1);
  const [year, setYear] = useState(now.getFullYear());
  const [filterSub, setFilterSub] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [expandedSubs, setExpandedSubs] = useState({});
  const [expandedCats, setExpandedCats] = useState({});

  const isAdmin = user && ['director','dist_admin','dist_head','tenant_admin','superadmin'].includes(user.role);

  const loadReport = useCallback(async () => {
    setLoading(true); setData(null); setAiText('');
    try {
      const params = { month, year };
      if(filterSub) params.sub_dist = filterSub;
      const r = await api.get('/reports/monthly-report', { params });
      setData(r.data);
      // Load AI separately
      setAiLoading(true);
      const token = localStorage.getItem('token');
      const aiResp = await fetch('/api/reports/monthly-report-ai', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Authorization:'Bearer '+token },
        body: JSON.stringify(r.data)
      });
      const aiData = await aiResp.json();
      if(aiData.success) {
        let txt = aiData.analysis||'';
        const thinkEnd = txt.lastIndexOf('</think>');
        if(thinkEnd!==-1) txt = txt.slice(thinkEnd+8).trim();
        setAiText(txt);
      }
    } catch(e) { console.error(e); }
    setLoading(false); setAiLoading(false);
  }, [month, year, filterSub]);

  const printReport = () => {
    const w = window.open('','_blank');
    const aiContent = aiText.split('\n').map(line=>{
      if(line.match(/^[0-9]+\.\s+[A-Z]|^[A-Z][A-Z\s]{4,}:$/)) return `<h3>${line}</h3>`;
      if(line.startsWith('- ')) return `<li>${line.slice(2)}</li>`;
      if(line.trim()==='') return '<br>';
      return `<p>${line}</p>`;
    }).join('');
    w.document.write(`<!DOCTYPE html><html><head><title>Monthly Report - ${data?.month_name} ${data?.year}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:0;margin:0;color:#1e293b;font-size:13px}
      .header{background:linear-gradient(135deg,#1e3a5f,#2563eb);color:white;padding:24px 32px}
      .header h1{margin:0 0 4px;font-size:20px} .header p{margin:0;opacity:.7;font-size:12px}
      .content{padding:20px 32px}
      .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px}
      .stat{background:#f8fafc;border-radius:8px;padding:12px;text-align:center}
      .stat .val{font-size:22px;font-weight:800} .stat .lbl{font-size:10px;color:#64748b}
      table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:12px}
      th{background:#f1f5f9;padding:8px;text-align:left;font-weight:700}
      td{padding:7px 8px;border-bottom:1px solid #f1f5f9}
      h2{font-size:14px;font-weight:800;margin:20px 0 8px;border-bottom:2px solid #2563eb;padding-bottom:4px}
      h3{font-size:13px;color:#1e293b;margin:12px 0 4px} li{margin:3px 0}
      .footer{margin-top:24px;padding:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#94a3b8;text-align:center}
      @media print{body{print-color-adjust:exact}}
    </style></head><body>
    <div class="header">
      <h1>📊 Monthly Issue Intelligence Report</h1>
      <p>${data?.month_name} ${data?.year} • PDIC Network Operations • Generated ${new Date().toLocaleString('en-IN')}</p>
    </div>
    <div class="content">
      <h2>EXECUTIVE SUMMARY</h2>
      <div class="grid">
        <div class="stat"><div class="val" style="color:#2563eb">${data?.summary.total}</div><div class="lbl">Total Tickets</div></div>
        <div class="stat"><div class="val" style="color:#15803d">${data?.summary.resolved}</div><div class="lbl">Resolved (${data?.summary.resolution_rate}%)</div></div>
        <div class="stat"><div class="val" style="color:#dc2626">${data?.summary.escalated}</div><div class="lbl">Escalated</div></div>
        <div class="stat"><div class="val" style="color:#d97706">${data?.summary.open}</div><div class="lbl">Pending/Open</div></div>
        <div class="stat"><div class="val" style="color:#7c3aed">${data?.summary.isp_raised}</div><div class="lbl">ISP Tickets</div></div>
        <div class="stat"><div class="val" style="color:#dc2626">${data?.summary.sla_breached}</div><div class="lbl">SLA Breached</div></div>
        <div class="stat"><div class="val" style="color:#0891b2">${data?.summary.avg_resolution_hrs}h</div><div class="lbl">Avg Resolution</div></div>
      </div>
      <h2>COMPLAINT CATEGORY ANALYSIS</h2>
      <table><thead><tr><th>Category</th><th>Total</th><th>Resolved</th><th>Rate</th><th>Avg Time</th><th>Escalated</th><th>ISP Raised</th></tr></thead>
      <tbody>${(data?.categories||[]).map(c=>`<tr><td>${c.name}</td><td>${c.total}</td><td>${c.resolved}</td><td>${c.resolution_rate}%</td><td>${c.avg_resolution_hrs}h</td><td>${c.escalated}</td><td>${c.isp_raised}</td></tr>`).join('')}</tbody></table>
      <h2>SUB-DISTRICT PERFORMANCE</h2>
      <table><thead><tr><th>Sub-District</th><th>Total</th><th>Resolved</th><th>Rate</th><th>Escalated</th><th>ISP Raised</th><th>Pending</th></tr></thead>
      <tbody>${(data?.sub_districts||[]).map(s=>`<tr><td>${s.code}</td><td>${s.total}</td><td>${s.resolved}</td><td>${s.resolution_rate}%</td><td>${s.escalated}</td><td>${s.isp_raised}</td><td>${s.open}</td></tr>`).join('')}</tbody></table>
      ${data?.repeat_customers?.length>0?`<h2>REPEAT COMPLAINANTS (3+ tickets)</h2>
      <table><thead><tr><th>Customer</th><th>Connection ID</th><th>Tickets</th><th>Categories</th></tr></thead>
      <tbody>${data.repeat_customers.map(c=>`<tr><td>${c.name}</td><td>${c.connection_id||'-'}</td><td>${c.count}</td><td>${[...new Set(c.categories)].join(', ')}</td></tr>`).join('')}</tbody></table>`:''}
      <h2>AI INTELLIGENCE ANALYSIS</h2>
      ${aiContent}
      <div class="footer">PDIC FibraDesk • Monthly Issue Intelligence Report • Powered by Groq AI</div>
    </div></body></html>`);
    w.document.close();
    setTimeout(()=>w.print(), 800);
  };

  if(!isAdmin) return <div style={{padding:'40px',textAlign:'center',color:'#94a3b8'}}>Access restricted to administrators.</div>;

  return (
    <div style={{padding:'20px',maxWidth:'1100px',margin:'0 auto'}}>
      {/* Page Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px',flexWrap:'wrap',gap:'10px'}}>
        <div>
          <div style={{fontSize:'20px',fontWeight:'800',color:'#1e293b'}}>📊 Monthly Issue Intelligence Report</div>
          <div style={{fontSize:'12px',color:'#64748b',marginTop:'2px'}}>Complaint analysis, resolution patterns & AI recommendations</div>
        </div>
        {data && <button onClick={printReport} style={{background:'#2563eb',color:'white',border:'none',borderRadius:'8px',padding:'10px 18px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>🖨️ Print / PDF</button>}
      </div>

      {/* Filters */}
      <div style={{...card,padding:'14px 16px',display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'center',marginBottom:'16px'}}>
        <select style={{...inp,width:'140px'}} value={month} onChange={e=>setMonth(parseInt(e.target.value))}>
          {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
        </select>
        <select style={{...inp,width:'90px'}} value={year} onChange={e=>setYear(parseInt(e.target.value))}>
          {[2024,2025,2026].map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <input style={{...inp,width:'130px'}} placeholder="Sub-District" value={filterSub} onChange={e=>setFilterSub(e.target.value)} />
        <button onClick={loadReport} disabled={loading} style={{background:'#2563eb',color:'white',border:'none',borderRadius:'8px',padding:'9px 20px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>
          {loading?'Loading...':'🔍 Generate Report'}
        </button>
      </div>

      {!data && !loading && (
        <div style={{textAlign:'center',padding:'60px',color:'#94a3b8'}}>
          <div style={{fontSize:'48px',marginBottom:'12px'}}>📊</div>
          <div style={{fontWeight:'600',fontSize:'15px'}}>Select month and click Generate Report</div>
          <div style={{fontSize:'12px',marginTop:'4px'}}>AI-powered monthly issue intelligence with action items</div>
        </div>
      )}

      {data && (
        <div>
          {/* Summary Cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:'10px',marginBottom:'16px'}}>
            {[
              {l:'Total Tickets',v:data.summary.total,c:'#2563eb',bg:'#eff6ff',icon:'🎫'},
              {l:`Resolved (${data.summary.resolution_rate}%)`,v:data.summary.resolved,c:'#15803d',bg:'#f0fdf4',icon:'✅'},
              {l:'Escalated',v:data.summary.escalated,c:'#dc2626',bg:'#fef2f2',icon:'🚨'},
              {l:'ISP Tickets',v:data.summary.isp_raised,c:'#7c3aed',bg:'#f5f3ff',icon:'📡'},
              {l:'Pending/Open',v:data.summary.open,c:'#d97706',bg:'#fffbeb',icon:'⏳'},
              {l:'SLA Breached',v:data.summary.sla_breached,c:'#dc2626',bg:'#fef2f2',icon:'⚠️'},
              {l:'Avg Resolution',v:data.summary.avg_resolution_hrs+'h',c:'#0891b2',bg:'#ecfeff',icon:'⏱️'},
            ].map(s=>(
              <div key={s.l} style={{background:s.bg,borderRadius:'10px',padding:'14px',textAlign:'center'}}>
                <div style={{fontSize:'18px',marginBottom:'4px'}}>{s.icon}</div>
                <div style={{fontSize:'22px',fontWeight:'800',color:s.c}}>{s.v}</div>
                <div style={{fontSize:'10px',color:'#64748b'}}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Category Analysis */}
          <div style={card}>
            <div style={{fontWeight:'800',fontSize:'14px',marginBottom:'12px',color:'#1e293b'}}>📋 Complaint Category Analysis</div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                <thead>
                  <tr style={{background:'#f8fafc'}}>
                    <th style={{padding:'8px 10px',textAlign:'left',fontWeight:'700'}}>Category</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Total</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Resolved</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Rate</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Avg Time</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Escalated</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>ISP Raised</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>SLA Breach</th>
                  </tr>
                </thead>
                <tbody>
                  {data.categories.map((c,i)=>{
                    const isExp = expandedCats[c.name];
                    return (
                      <React.Fragment key={i}>
                        <tr onClick={()=>setExpandedCats(p=>({...p,[c.name]:!p[c.name]}))}
                          style={{borderTop:'1px solid #f1f5f9',cursor:'pointer',background:isExp?'#f8fafc':'white'}}>
                          <td style={{padding:'8px 10px',fontWeight:'700'}}>{c.name} <span style={{color:'#94a3b8',fontSize:'10px'}}>{isExp?'▲':'▼'}</span></td>
                          <td style={{padding:'8px 10px',textAlign:'center',fontWeight:'700'}}>{c.total}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'#15803d'}}>{c.resolved}</td>
                          <td style={{padding:'8px 10px',textAlign:'center'}}>
                            <span style={{background:c.resolution_rate>=80?'#dcfce7':c.resolution_rate>=60?'#fef9c3':'#fef2f2',color:c.resolution_rate>=80?'#15803d':c.resolution_rate>=60?'#854d0e':'#dc2626',padding:'2px 8px',borderRadius:'20px',fontWeight:'700'}}>
                              {c.resolution_rate}%
                            </span>
                          </td>
                          <td style={{padding:'8px 10px',textAlign:'center'}}>{c.avg_resolution_hrs}h</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:c.escalated>0?'#dc2626':'#94a3b8'}}>{c.escalated}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:c.isp_raised>0?'#7c3aed':'#94a3b8'}}>{c.isp_raised}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:c.sla_breached>0?'#dc2626':'#94a3b8'}}>{c.sla_breached}</td>
                        </tr>
                        {isExp && c.top_resolutions?.length>0 && (
                          <tr style={{background:'#f8fafc'}}>
                            <td colSpan={8} style={{padding:'8px 16px'}}>
                              <div style={{fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>📝 Common Resolutions:</div>
                              {c.top_resolutions.map((r,ri)=>(
                                <div key={ri} style={{fontSize:'11px',color:'#64748b',padding:'3px 0',borderBottom:'1px dashed #e2e8f0'}}>• {r}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sub-District Performance */}
          <div style={card}>
            <div style={{fontWeight:'800',fontSize:'14px',marginBottom:'12px',color:'#1e293b'}}>🏘️ Sub-District Performance</div>
            <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
              {data.sub_districts.map((s,i)=>{
                const isExp = expandedSubs[s.code];
                const health = s.resolution_rate>=80?'✅':s.resolution_rate>=60?'🟡':'🔴';
                const bc = s.resolution_rate>=80?'#e2e8f0':s.resolution_rate>=60?'#fcd34d':'#fca5a5';
                const hbg = s.resolution_rate>=80?'#f8fafc':s.resolution_rate>=60?'#fffbeb':'#fef2f2';
                return (
                  <div key={i} style={{borderRadius:'8px',border:`1px solid ${bc}`,overflow:'hidden'}}>
                    <div onClick={()=>setExpandedSubs(p=>({...p,[s.code]:!p[s.code]}))}
                      style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',cursor:'pointer',background:hbg,userSelect:'none'}}>
                      <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                        <span style={{fontSize:'16px'}}>{health}</span>
                        <div>
                          <span style={{fontWeight:'700',fontSize:'13px',color:'#1e293b'}}>{s.code}</span>
                          <div style={{display:'flex',gap:'10px',marginTop:'2px',flexWrap:'wrap'}}>
                            <span style={{fontSize:'10px',color:'#64748b'}}>🎫 {s.total} tickets</span>
                            <span style={{fontSize:'10px',color:'#15803d'}}>✅ {s.resolution_rate}% resolved</span>
                            {s.escalated>0&&<span style={{fontSize:'10px',color:'#dc2626',fontWeight:'700'}}>🚨 {s.escalated} escalated</span>}
                            {s.isp_raised>0&&<span style={{fontSize:'10px',color:'#7c3aed',fontWeight:'700'}}>📡 {s.isp_raised} ISP</span>}
                            {s.open>0&&<span style={{fontSize:'10px',color:'#d97706',fontWeight:'700'}}>⏳ {s.open} pending</span>}
                          </div>
                        </div>
                      </div>
                      <span style={{fontSize:'12px',color:'#94a3b8',transform:isExp?'rotate(180deg)':'rotate(0deg)',display:'inline-block',transition:'transform .2s'}}>▼</span>
                    </div>
                    {isExp && (
                      <div style={{padding:'10px 14px',borderTop:`1px solid ${bc}`,background:'#fafafa'}}>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(100px,1fr))',gap:'8px'}}>
                          {[
                            {l:'Total',v:s.total,c:'#2563eb'},{l:'Resolved',v:s.resolved,c:'#15803d'},
                            {l:'Escalated',v:s.escalated,c:'#dc2626'},{l:'ISP Raised',v:s.isp_raised,c:'#7c3aed'},
                            {l:'Pending',v:s.open,c:'#d97706'},{l:'SLA Breach',v:s.sla_breached,c:'#dc2626'}
                          ].map(x=>(
                            <div key={x.l} style={{background:'white',borderRadius:'6px',padding:'8px',textAlign:'center',border:'1px solid #e2e8f0'}}>
                              <div style={{fontSize:'16px',fontWeight:'800',color:x.c}}>{x.v}</div>
                              <div style={{fontSize:'9px',color:'#64748b'}}>{x.l}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Repeat Customers */}
          {data.repeat_customers?.length>0 && (
            <div style={card}>
              <div style={{fontWeight:'800',fontSize:'14px',marginBottom:'12px',color:'#dc2626'}}>⚠️ Repeat Complainants (3+ tickets this month)</div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead><tr style={{background:'#fef2f2'}}>
                    <th style={{padding:'8px 10px',textAlign:'left'}}>Customer</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Connection ID</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Tickets</th>
                    <th style={{padding:'8px 10px',textAlign:'left'}}>Categories</th>
                  </tr></thead>
                  <tbody>
                    {data.repeat_customers.map((c,i)=>(
                      <tr key={i} style={{borderTop:'1px solid #f1f5f9'}}>
                        <td style={{padding:'8px 10px',fontWeight:'600'}}>{c.name}<br/><span style={{fontSize:'10px',color:'#94a3b8'}}>{c.phone}</span></td>
                        <td style={{padding:'8px 10px',textAlign:'center',fontFamily:'monospace'}}>{c.connection_id||'-'}</td>
                        <td style={{padding:'8px 10px',textAlign:'center'}}><span style={{background:'#fef2f2',color:'#dc2626',fontWeight:'800',padding:'2px 8px',borderRadius:'20px'}}>{c.count}</span></td>
                        <td style={{padding:'8px 10px',fontSize:'11px',color:'#64748b'}}>{[...new Set(c.categories)].join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* AI Intelligence Report */}
          <div style={{background:'white',borderRadius:'12px',border:'1px solid #e2e8f0',overflow:'hidden',boxShadow:'0 2px 10px rgba(0,0,0,0.08)'}}>
            <div style={{background:'linear-gradient(135deg,#1e293b,#334155)',padding:'16px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{color:'#94a3b8',fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'3px'}}>AI Intelligence Analysis</div>
                <div style={{color:'white',fontSize:'15px',fontWeight:'800'}}>🤖 {data.month_name} {data.year} — Strategic Report</div>
                <div style={{color:'#94a3b8',fontSize:'11px',marginTop:'2px'}}>Powered by Groq AI</div>
              </div>
            </div>
            <div style={{padding:'20px 24px'}}>
              {aiLoading && (
                <div style={{textAlign:'center',padding:'30px',color:'#64748b'}}>
                  <div style={{fontSize:'24px',marginBottom:'8px'}}>🤖</div>
                  <div style={{fontWeight:'600'}}>AI is analyzing {data.summary.total} tickets...</div>
                  <div style={{fontSize:'12px',marginTop:'4px',color:'#94a3b8'}}>Generating strategic insights and action plan</div>
                </div>
              )}
              {aiText && aiText.split('\n').map((line,i)=>{
                if(line.match(/^[0-9]+\.\s+[A-Z]/)||line.match(/^[A-Z][A-Z\s]{4,}:$/)||line.match(/^#{1,3}\s/))
                  return <h3 key={i} style={{fontSize:'13px',fontWeight:'800',color:'#1e293b',margin:'14px 0 6px',paddingBottom:'4px',borderBottom:'1px solid #f1f5f9',textTransform:'uppercase',letterSpacing:'.3px'}}>{line.replace(/^#{1,3}\s/,'').replace(/^[0-9]+\.\s/,'')}</h3>;
                if(line.startsWith('- ')||line.startsWith('• '))
                  return <div key={i} style={{display:'flex',gap:'8px',margin:'4px 0',fontSize:'13px',color:'#334155',lineHeight:'1.6'}}><span style={{color:'#2563eb',flexShrink:0}}>•</span><span>{line.slice(2)}</span></div>;
                if(line.trim()==='') return <div key={i} style={{height:'8px'}}/>;
                return <p key={i} style={{margin:'4px 0',fontSize:'13px',color:'#475569',lineHeight:'1.7'}}>{line}</p>;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
